import React, { FC, useCallback, useState } from 'react';
import { IonPage } from '@ionic/react';
import { AppHeader_v2 as AppHeader, IconArrowLeft, ScreenDrumPickerFormFeature } from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { userSelector } from '../../../redux/user/selector';
import useToast from '../../../context/ToastContext/ToastContext';
import { updateUserPersonalData } from '../../../api/user';
import { setUser } from '../../../redux/user/reducer';
import { useUpdateProfile } from '../../../shared/hooks/useUpdateProfile';

const ProfileDoB: FC<RouteComponentProps> = ({ history }) => {
  const [isLoading, setIsLoading] = useState(false);

  const user = useSelector(userSelector);
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { successfullyUpdatedProfile } = useUpdateProfile();

  const {
    t,
    i18n: { language },
  } = useTranslation('profile');

  const handleSubmit = useCallback(async (value: Record<string, string>) => {
    try {
      if (!DateTime.fromFormat(`${value.day}.${value.month}.${value.year}`, 'dd.MM.yyyy').isValid) {
        throw new Error(t('app:errors.date_invalid'));
      }

      setIsLoading(true);
      const { data, error, message } = await updateUserPersonalData({
        dob: `${value.day}.${value.month}.${value.year}`,
      });

      if (error) {
        throw new Error(message ? t(message) : 'Something went wrong');
      }

      dispatch(setUser(data));
      successfullyUpdatedProfile(true);
    } catch (e: any) {
      showToast({ text: e?.message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <IonPage>
      <AppHeader className="flex flex-row items-start cursor-pointer">
        <IconArrowLeft onClick={() => history.back()} />
      </AppHeader>
      <ScreenDrumPickerFormFeature
        title={t('personal_details.date_of_birth.subtitle')}
        subtitle={t('personal_details.date_of_birth.description')}
        compareBy={'value'}
        selections={{
          day: Array.from({ length: 31 }, (_, i) => ({
            value: String(i + 1).padStart(2, '0'),
            label: String(i + 1).padStart(2, '0'),
          })),
          month: Array.from({ length: 12 }, (_, i) => ({
            value: DateTime.fromFormat((i + 1).toString(), 'M').toFormat('MM'),
            label: DateTime.local()
              .set({ month: i + 1 })
              .setLocale(language)
              .toFormat('LLLL'),
          })),
          year: Array.from({ length: DateTime.local().year - 18 - 1940 + 1 }, (_, i) => ({
            value: (1940 + i).toString(),
            label: (1940 + i).toString(),
          })),
        }}
        defaults={{
          day: user?.dob ? DateTime.fromFormat(user.dob, 'dd.MM.yyyy').toFormat('dd') : '01',
          month: user?.dob ? DateTime.fromFormat(user.dob, 'dd.MM.yyyy').toFormat('MM') : '01',
          year: user?.dob
            ? DateTime.fromFormat(user.dob, 'dd.MM.yyyy').toFormat('yyyy')
            : (DateTime.local().year - 23).toString(),
        }}
        onChange={handleSubmit}
        isLoading={isLoading}
        buttonText={t('save_button')}
      />
    </IonPage>
  );
};

export default ProfileDoB;
